import React, { useContext, useEffect, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { getOption, updateOption } from 'app/api/PublicOptionsService';
import useRequest from 'app/hooks/useRequest';
import UserContext from 'app/context/UserContext';
import { toast } from 'react-toastify';
import Divider from 'components/common/Divider';
import { upload } from 'app/api/UploadsService';

export default function InvestmentForm() {
  const { user } = useContext(UserContext);
  const { data, fetch } = useRequest();
  const { response, send, loading } = useRequest();
  const [uploading, setUploading] = useState(false);

const [investment, setInvestment] = useState({
  title: '...',
  subTitle: '...',
  sections: Array(3).fill(null).map(() => ({
    title: '...',
    description: '...',
    images: []
  })),
  headerTitle: '...',
  headerSubTitle: '...',
  sectionMessage: '...',
  adminTitle: '',
  adminDescription: ''
});


  // helper to safely update section
  const updateSection = (idx, updater) => {
    setInvestment(prev => {
      const sections = [...prev.sections];
      sections[idx] = updater(sections[idx]);
      return { ...prev, sections };
    });
  };

  // MULTIPLE FILE UPLOAD
  const handleSectionFileUpload = async (files, idx) => {
    if (!files?.length) return;

    setUploading(true);
    try {
      const uploadedImages = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const res = await upload(user.token, formData);
        const { fileType, formatType, relativePath } = res.data || {};

        if (relativePath) {
          uploadedImages.push({ fileType, formatType, relativePath });
        }
      }

      if (uploadedImages.length) {
        updateSection(idx, section => ({
          ...section,
          images: [...section?.images, ...uploadedImages]
        }));

        toast.success('Files uploaded successfully');
      } else {
        toast.error('Upload failed');
      }

    } catch (err) {
      console.error('Upload error:', err);
      toast.error('File upload failed');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (sectionIdx, imageIdx) => {
    updateSection(sectionIdx, section => ({
      ...section,
      images: section.images.filter((_, i) => i !== imageIdx)
    }));
  };

  const handleSubmit = e => {
    e.preventDefault();
    send(
      updateOption(user.token, 'landing_investment', JSON.stringify(investment))
    );
  };

  useEffect(() => {
    fetch(getOption('landing_investment'));
  }, []);

  // SAFE PARSE
  useEffect(() => {
  if (!data?.optionValue) return;

  try {
    const parsed = JSON.parse(data.optionValue);

    // 🔥 MIGRATE OLD STRUCTURE → NEW STRUCTURE
    const normalizedSections = (parsed.sections || []).map(section => {
      // if already new structure → keep it
      if (Array.isArray(section.images)) return section;

      // if old structure had single image → convert to images[]
      if (section.relativePath) {
        return {
          title: section.title || '',
          description: section.description || '',
          images: [
            {
              fileType: section.fileType,
              formatType: section.formatType,
              relativePath: section.relativePath
            }
          ]
        };
      }

      // fallback empty section
      return {
        title: section.title || '',
        description: section.description || '',
        images: []
      };
    });

    setInvestment({
      title: parsed.title || '',
      subTitle: parsed.subTitle || '',
      headerTitle: parsed.headerTitle || '',
      headerSubTitle: parsed.headerSubTitle || '',
      sectionMessage: parsed.sectionMessage || '',
      adminTitle: parsed.adminTitle || '',
      adminDescription: parsed.adminDescription || parsed.adminMessage || '',
      sections: normalizedSections
    });

  } catch (err) {
    console.warn('Invalid investment JSON', err);
  }
}, [data]);


  useEffect(() => {
    if (response?.status === 200) {
      toast.success('Saved successfully', { theme: 'colored' });
    }
  }, [response]);

  const renderSection = (section, idx) => (
    <Col xl={4} key={idx}>
      <Divider className="mt-5 mb-3">
        <b>Section {idx + 1}</b>
      </Divider>

      <Form.Group className="mb-3">
        <Form.Label>Title</Form.Label>
        <Form.Control
          value={section.title}
          onChange={e =>
            updateSection(idx, s => ({ ...s, title: e.target.value }))
          }
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={5}
          value={section.description}
          onChange={e =>
            updateSection(idx, s => ({ ...s, description: e.target.value }))
          }
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Upload Images / Media</Form.Label>
        <Form.Control
          type="file"
          multiple
          accept="image/*,audio/*,video/*"
          onChange={e => handleSectionFileUpload(e.target.files, idx)}
        />
        <div class="form-text">
        Note: Please upload an image with dimensions <strong style={{color:"orange", fontWeight:"bold"}}>960 × 1440 px</strong>.
    </div>
      </Form.Group>

      {/* Image previews */}
      {section?.images?.length > 0 && (
        <Row className="g-2">
          {section.images.map((img, imageIdx) => (
            <Col xs={6} key={imageIdx}>
              <div style={{ position: 'relative' }}>
                {img.fileType === 'image' && (
                  <img
                    src={img.relativePath}
                    alt=""
                    style={{
                      width: '100%',
                      height: 120,
                      objectFit: 'cover',
                      borderRadius: 8
                    }}
                  />
                )}
                <Button
                  size="sm"
                  variant="danger"
                  style={{
                    position: 'absolute',
                    top: 5,
                    right: 5,
                    padding: '2px 6px'
                  }}
                  onClick={() => removeImage(idx, imageIdx)}
                >
                  ✕
                </Button>
              </div>
            </Col>
          ))}
        </Row>
      )}
    </Col>
  );

  return (
    <Form onSubmit={handleSubmit}>
      <Row>
        <Col xl={12}>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={investment.title}
              onChange={e => setInvestment(p => ({ ...p, title: e.target.value }))}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Subtitle</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={investment.subTitle}
              onChange={e => setInvestment(p => ({ ...p, subTitle: e.target.value }))}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Header Title</Form.Label>
            <Form.Control
              value={investment.headerTitle}
              onChange={e => setInvestment(p => ({ ...p, headerTitle: e.target.value }))}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Header Subtitle</Form.Label>
            <Form.Control
              as="textarea"
              rows={10}
              value={investment.headerSubTitle}
              onChange={e => setInvestment(p => ({ ...p, headerSubTitle: e.target.value }))}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Section Message</Form.Label>
            <Form.Control
              value={investment.sectionMessage}
              onChange={e => setInvestment(p => ({ ...p, sectionMessage: e.target.value }))}
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
        <Form.Label>Admin Title</Form.Label>
        <Form.Control
          value={investment.adminTitle}
          placeholder="Enter Title"
          onChange={e =>
            setInvestment(p => ({ ...p, adminTitle: e.target.value }))
          }
        />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Admin Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Enter Description"
              value={investment.adminDescription}
              onChange={e =>
                setInvestment(p => ({ ...p, adminDescription: e.target.value }))
              }
            />
          </Form.Group>


        </Col>
      </Row>

      <Row>
        {investment.sections.map((section, idx) => renderSection(section, idx))}
      </Row>

      <Divider className="my-4" />

      <Button type="submit" disabled={loading || uploading}>
        Save
      </Button>

      {(loading || uploading) && (
        <Spinner animation="grow" size="sm" className="ms-2" />
      )}
    </Form>
  );
}
